import { ImportTemplateSource } from '../model/enum';

/** Columns supplied on the upload screen import model — excluded from mapping and unmapped lists. */
export const IMPORT_MODULE_PROVIDED_FIELDS: Record<number, string[]> = {
  [ImportTemplateSource.Companies]: ['Salesperson', 'Saleperson', 'Alt Salesperson', 'Alternate Salesperson'],
  [ImportTemplateSource.Vendor]: ['Salesperson', 'Saleperson', 'Alt Salesperson', 'Alternate Salesperson', "Buyer's Rep"],
  [ImportTemplateSource.Requirement]: ['Company', 'Contact', 'Salesperson', 'Saleperson'],
  [ImportTemplateSource.Inventory]: ['Vendor', 'Contact', 'Company'],
  [ImportTemplateSource.Consignment]: ['Company', 'Consignor', 'Contact', 'Payout Percentage'],
  [ImportTemplateSource.VendorParts]: ['Vendor', 'Contact'],
  [ImportTemplateSource.OEMExcess]: ['Customer', 'Contact'],
};

export function normalizeImportHeader(value: string): string {
  return (value ?? '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function isImportModelProvidedField(sourceId: number, columnName: string): boolean {
  const provided = IMPORT_MODULE_PROVIDED_FIELDS[sourceId] ?? [];
  const normalized = normalizeImportHeader(columnName);
  return provided.some((name) => normalizeImportHeader(name) === normalized);
}

export function filterMappableSystemColumns(columns: string[], sourceId: number): string[] {
  return columns.filter((col) => !isImportModelProvidedField(sourceId, col));
}

export function filterUnmappedUploadedColumns(headers: string[], sourceId: number, mappedColumns: string[] = []): string[] {
  const mappedNormalized = new Set(mappedColumns.map(normalizeImportHeader));
  return headers.filter((header) => {
    const trimmed = header?.trim();
    if (!trimmed || trimmed === '-Select Field-') return false;
    if (isImportModelProvidedField(sourceId, trimmed)) return false;
    return !mappedNormalized.has(normalizeImportHeader(trimmed));
  });
}

/** System columns aligned with Import_Module_Fields (2026). */
export const IMPORT_MODULE_SYSTEM_COLUMNS: Record<number, string[]> = {
  [ImportTemplateSource.Companies]: [
    'Company Name', 'Sales Stage', 'Company Phone Number', 'Company Email Address', 'Company Fax Number',
    'Company Type', 'Company Timezone', 'Company State', 'Company Country', 'Website', 'Rating', 'Tags', 
    'Address Type', 'Address Name', 'Address City', 'Address Country', 'Address Phone Number', 'Postal Code', 
    'Address Line 1', 'Address Line 2', 'Mark as default for Type', 'Address State', 'Address VAT/RFC/CNPJ',
    'Scope', 'Details VAT/RFC/CNPJ', 'SIC Code', 'NAICS Code', 'Reseller ID', 'Reseller ID Expiration Date',
    'Details Notes', 'D&B Number', 'Industry', 'Entity Type', 'Employees', 'Parent Company', 'Currency',
    'Revenue', 'Tax ID', 'Contact First Name', 'Contact Last Name', 'Title', 'Gender', 'Department', 'Location',
    'Contact Timezone', 'Contact Email', 'Main Office Number + Extension', 'Contact Direct Number',
    'Contact Mobile Number', 'Contact Fax Number', 'Skype', 'Xing', 'LinkdIn', 'Contact Notes'
  ],
  [ImportTemplateSource.Vendor]: [
    'Vendor Name', 'Email Address', 'Phone Number', 'Fax Number', 'Vendor State', 'Country', 'Website', 'Timezone',
    'Rating', 'Type', 'Tags', 'Address Name', 'Address Type', 'Address State', 'Address Line 1',
    'Address Line 2', 'Address Country', 'Address City', 'Address Phone Number', 'Postal Code', 'Address VAT/RFC/CNPJ', 'D&B Number', 'Industry',
    'SIC Code', 'NAICS Code', 'Scope','Details VAT/RFC/CNPJ', 'Entity Type', 'Employees', 'Parent Company', 'Currency', 'Tax ID',
    'Reseller ID', 'Reseller ID Expiration Date', 'Vendor Specialty', 'Notes', 'Contact First Name',
    'Contact Last Name', 'Title', 'Gender', 'Department', 'Location', 'Contact Timezone', 'Contact Email', 'Main Office Number',
    'Direct Number', 'Contact Mobile Number', 'Contact Fax Number', 'Skype', 'Xing', 'LinkedIn', 'Contact Notes'
  ],
  [ImportTemplateSource.CodeCrosswalk]: [
    'SIC Code', 'SIC Description', 'NAICS Code', 'NAICS Year', 'NAICS Description', 'Industry'
  ],
  [ImportTemplateSource.Parts]: [
    'MPN', 'MFR', 'Part Status', 'Internal Part Number', 'Description', 'Standard Packaging', 'Device package',
    'Temp Grade', 'SPQ', 'MOQ', 'Mount Type', 'Notes', 'RoHS', 'REACH', 'MSL', 'COO', 'NSN', 'HTS', 'Sched B',
    'ECCN', 'Tags', 'Category Group', 'Parent Category', 'Sub Category', 'Pin Count', 'Lead Shape', 'Dimensions',
    'Storage Temp', 'Backlighting', 'Brightness', 'Resolution', 'Contrast Ratio', 'Supply Voltage', 'Cost',
    'Asset Account', 'Expense Account', 'List Price', 'Resale', 'Discount Name', 'Qty Related', 'Name', 'SKU', 'UOM',
  ],
  [ImportTemplateSource.Inventory]: [
    'MPN', 'MFR','Warehouse', 'Location', 'Received In',
    'Unit Cost', 'Received Packaging', 'Notes'
  ],
  [ImportTemplateSource.Consignment]: [
    'Contract End', 'MPN', 'MFR', 'Original Price/Unit',
    'Warehouse', 'CPN', 'Location', 'Received In', 'Received Packaging', 'Notes',
    'Payout Price/Unit', 'Paid as percentage of sale'
  ],
  [ImportTemplateSource.VendorParts]: [
    'Expiration Date', 'MPN', 'MFR', 'VPN', 'Quantity', 'Unit Cost', 'RoHS',
    'Date Code', 'Lot Code', 'Line Notes'
  ],
  [ImportTemplateSource.OEMExcess]: [
    'Expiration Date', 'MPN', 'MFR', 'CPN', 'Quantity', 'Unit Cost', 'RoHS','Date Code', 
    'Lot Code', 'Line Notes'
  ],
  [ImportTemplateSource.Requirement]: [
    'Req Source', 'Customer Note', 'Customer RFQ No.', 'Priority',
    'Requirement Date', 'MPN', 'MFR', 'Qty', 'Date Code Year', 'Date Code Week', 'Customer PN', 
    'NSN', 'RoHS', 'Condition', 'Item Pkg', 'Description', 'Revision', 'COO', 'Testing', 'Customer target', 
    'Currency', 'Exchange Rate', 'Converted Target', 'Target Buy Price', 'Need Date', 'Opportunity Type', 
    'Project Approved', 'Est. Start Date', 'Bid Deadline', 'End Customer/Industry', 'Project Name',
    'Close Probability', 'Application', 'Opportunity Notes', 'Opportunity Buy Type'
  ],
  [ImportTemplateSource.Offer]: [
    'Date of Offer', 'Vendor', 'Contact', 'Comments', 'Qty Offered', 'Qty Available', ' Cost', 'Currency',
    'Exchange Rate', 'Converted Cost', 'Testing Decision', 'MPN', 'MFR', 'Date Code Year', 'Date Code Week', 'RoHS',
    'NSN', 'Condition', 'Item Pkg', 'COO', 'Description ', 'Revision', 'Lot', 'Vendor Lead Time', 'Warranty',
    'Category Group', 'Parent Category', 'Category ', 'Sub-Category', 'Label', 'Trace Document ', 'Traceable Part ',
    'Recent Test Report', 'SPQ', 'MOQ', 'HTS', 'Schedule B', 'ECCN', 'Temp Range', 'Part Status',
  ],
};

/** Red-font columns from Import_Module_Fields.xlsx — prefer getModuleFields API at runtime. */
export const IMPORT_MODULE_REQUIRED_FIELDS: Record<number, string[]> = {
  [ImportTemplateSource.Companies]: [
    'Company Name', 'Sales Stage', 'Company Type', 'Company Country', 'Contact First Name',
    'Contact Last Name', 'Address Type', 'Address Name', 'Address Country'
  ],
  [ImportTemplateSource.Vendor]: [
    'Vendor Name', 'Country', 'Type', 'Contact First Name', 'Contact Last Name', 'Address Type', 'Address Name', 'Address Country'
  ],
  [ImportTemplateSource.CodeCrosswalk]: [
    'SIC Code', 'SIC Description', 'NAICS Code', 'NAICS Year', 'NAICS Description', 'Industry',
  ],
  [ImportTemplateSource.Parts]: ['MPN', 'MFR'],
  
  [ImportTemplateSource.Inventory]: ['MPN', 'MFR'],
  [ImportTemplateSource.Consignment]: ['MPN', 'MFR'],
  [ImportTemplateSource.VendorParts]: ['MPN', 'MFR'],
  [ImportTemplateSource.OEMExcess]: ['MPN', 'MFR'],
  [ImportTemplateSource.Requirement]: [
    'MPN', 'MFR', 'Qty', 'Date Code Year', 'Date Code Week',
    'RoHS', 'Condition', 'Need Date', 'Opportunity Type',
  ],
  [ImportTemplateSource.Offer]: [
    'Date of Offer', 'Vendor', 'Qty Offered', 'Cost', 'Currency', 'Testing Decision',
    'MPN', 'MFR', 'Date Code Year', 'Date Code Week', 'RoHS', 'Item Pkg', 'COO',
    'Vendor Lead Time', 'Warranty', 'Label',
  ],
};

export enum ImportMappingStatusUi {
  AutoMapped = 1,
  ManualMapped = 2,
  OptionalUnmapped = 3,
  RequiredMissing = 4,
}

export function applyAutoMapToRows(
  rowData: any[],
  autoMapResponse: any,
  uploadedHeaderOptions: string[],
  sourceId?: number
): { rowData: any[]; unmappedColumns: string[]; canProceed: boolean; blockImport: boolean } {
  const mappingRows = autoMapResponse?.mappingRows ?? [];
  const bySystem = new Map(mappingRows.map((r: any) => [r.systemColumn, r]));

  const updated = rowData
    .filter((row) => sourceId == null || !isImportModelProvidedField(sourceId, row.systemColumn))
    .map((row) => {
    const match:any = bySystem.get(row.systemColumn);
    if (!match) return row;
    const resolvedDefault = match.resolvedDefaultValue ?? '';
    const mapped = match.mappedColumn ?? '';
    return {
      ...row,
      mappedColumn: mapped,
      mappingStatus: match.mappingStatus,
      matchType: match.matchType,
      formTab: match.formTab ?? row.formTab,
      allowsDefaultValue: match.allowsDefaultValue ?? row.allowsDefaultValue,
      defaultValue: match.defaultValue ?? row.defaultValue,
      resolvedDefaultValue: resolvedDefault,
      status: !!mapped || !!resolvedDefault,
      map: uploadedHeaderOptions,
      isRequired: match.isRequired ?? row.isRequired,
    };
  });

  const mappedColumns = updated
    .map((row) => row.mappedColumn?.trim())
    .filter((col) => !!col && col !== '-Select Field-');

  const rawUnmapped = autoMapResponse?.unmappedUploadedColumns ?? [];
  const unmappedColumns = sourceId != null
    ? filterUnmappedUploadedColumns(rawUnmapped.length ? rawUnmapped : uploadedHeaderOptions, sourceId, mappedColumns)
    : rawUnmapped;

  return {
    rowData: updated,
    unmappedColumns,
    canProceed: autoMapResponse?.canProceed ?? false,
    blockImport: autoMapResponse?.blockImport ?? !(autoMapResponse?.canProceed ?? false),
  };
}
